
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

interface HeaderData {
  avatar: string | null;
  name: string;
}

export function useHeaderData(initialAvatar: string | null = null, initialName: string = "JD") {
  const [avatar, setAvatar] = useState<string | null>(initialAvatar);
  const [name, setName] = useState<string>(initialName);

  useEffect(() => {
    const fetchUserProfile = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: profile } = await supabase
        .from('profiles')
        .select('full_name, avatar_url')
        .eq('id', user.id)
        .single();

      if (profile) {
        if (profile.avatar_url && profile.avatar_url !== "/placeholder.svg") {
          setAvatar(profile.avatar_url);
          localStorage.setItem('userAvatar', profile.avatar_url);
        } else {
          setAvatar(null);
          localStorage.removeItem('userAvatar');
        }
        
        if (profile.full_name) {
          setName(profile.full_name);
          localStorage.setItem('userName', profile.full_name);
        }
      }
    };

    fetchUserProfile();

    // Subscribe to profile changes
    const channel = supabase.channel('public:profiles')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'profiles'
        },
        async (payload) => {
          const { data: { user } } = await supabase.auth.getUser();
          if (user && payload.new.id === user.id) {
            if (payload.new.avatar_url && payload.new.avatar_url !== "/placeholder.svg") {
              setAvatar(payload.new.avatar_url);
              localStorage.setItem('userAvatar', payload.new.avatar_url);
            } else {
              setAvatar(null);
              localStorage.removeItem('userAvatar');
            }
            
            if (payload.new.full_name) {
              setName(payload.new.full_name);
              localStorage.setItem('userName', payload.new.full_name);
            }
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  // Update state if props change
  useEffect(() => {
    if (initialAvatar !== avatar) setAvatar(initialAvatar);
    if (initialName !== name) setName(initialName);
  }, [initialAvatar, initialName]);

  return { avatar, name };
}
